import { Component, OnInit, Inject, Optional } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { LanguageService } from '../../../shared/services/language.service';
import { ProfileService } from '../../../shared/services/profile.service';

@Component({
  standalone: false,
  selector: 'app-profile-selector-dialog',
  templateUrl: './profile-selector-dialog.component.html',
  styleUrls: ['./profile-selector-dialog.component.css']
})
export class ProfileSelectorDialogComponent implements OnInit {

  variables: any;
  local_data: any;
  profiles: any[] = [];
  centers: string[] = [];
  filteredProfiles: any[] = [];
  selectedCenter: string | null = null;
  selectedProfileId: string | null = null;
  currentProfileName: string = '-';
  currentProfileCostPerHour: string = '-';
  currentProfileCenter: string = '-';
  isLoading: boolean = false;

  constructor(
    public dialogRef: MatDialogRef<ProfileSelectorDialogComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: any,
    private profileService: ProfileService,
    private languageService: LanguageService
  ) {
    this.local_data = { ...data };
  }

  ngOnInit(): void {
    this.languageService.translations$.subscribe((vars: any) => {
      this.variables = vars;
    });

    // Set initial selected profile
    this.selectedProfileId = this.local_data?.profileId || null;

    // Load available profiles
    this.loadProfiles();

    // Set current profile name for display
    if (this.local_data?.currentProfileName) {
      this.currentProfileName = this.local_data.currentProfileName;
    }
    // Set current profile cost per hour for display
    if (this.local_data?.currentProfileCostPerHour) {
      this.currentProfileCostPerHour = this.local_data.currentProfileCostPerHour;
    }
    //Set current profile center for display
    if (this.local_data?.currentProfileCenter) {
      this.currentProfileCenter = this.local_data.currentProfileCenter;
    }
  }

  loadProfiles(): void {
    this.isLoading = true;
    this.profileService.getAllProfiles().subscribe({
      next: (profiles: any[]) => {
        this.profiles = profiles;
        this.centers = Array.from(new Set(this.profiles.map((profile) => this.getProfileCenter(profile)).filter((center) => !!center))).sort();
        this.initializeCenterAndProfileSelection();
        this.isLoading = false;
      },
      error: (err) => {
        console.error('Error loading profiles:', err);
        this.isLoading = false;
      }
    });
  }

  onProfileSelect(profileId: string | null): void {
    this.selectedProfileId = profileId;
  }

  onCenterSelect(center: string | null): void {
    this.selectedCenter = center;
    this.updateFilteredProfiles();

    if (this.selectedProfileId && !this.filteredProfiles.some((profile) => profile.id === this.selectedProfileId)) {
      this.selectedProfileId = null;
    }
  }

  onConfirm(): void {
    const selectedProfile = this.profiles.find(p => p.id === this.selectedProfileId);
    this.dialogRef.close({
      profileId: this.selectedProfileId,
      profileName: selectedProfile?.name || ''
    });
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  getProfileName(profileId: string): string {
    const profile = this.profiles.find(p => p.id === profileId);
    return profile?.name || '-';
  }

  getProfileInfo(profileId: string): string {
    const profile = this.profiles.find(p => p.id === profileId);
    if (!profile) return '-';
    return `${profile.name} (${profile.costPerHour || 0}€/h)`;
  }

  private initializeCenterAndProfileSelection(): void {
    if (this.selectedProfileId) {
      const selectedProfile = this.profiles.find((profile) => profile.id === this.selectedProfileId);
      if (selectedProfile) {
        this.selectedCenter = this.getProfileCenter(selectedProfile);
      }
    }

    if (!this.selectedCenter && this.local_data?.currentProfileCenter) {
      this.selectedCenter = this.local_data.currentProfileCenter;
    }

    this.updateFilteredProfiles();

    if (this.selectedProfileId && !this.filteredProfiles.some((profile) => profile.id === this.selectedProfileId)) {
      this.selectedProfileId = null;
    }
  }

  private updateFilteredProfiles(): void {
    if (!this.selectedCenter) {
      this.filteredProfiles = [];
      return;
    }

    this.filteredProfiles = this.profiles
      .filter((profile) => this.getProfileCenter(profile) === this.selectedCenter)
      .sort((a, b) => this.naturalSort(a.name, b.name));
  }

  private naturalSort(a: string, b: string): number {
    // Convert to lowercase for case-insensitive comparison
    const aStr = String(a).toLowerCase();
    const bStr = String(b).toLowerCase();

    // Split strings into numeric and non-numeric parts
    const aTokens = aStr.match(/(\d+|\D+)/g) || [];
    const bTokens = bStr.match(/(\d+|\D+)/g) || [];

    // Compare tokens
    for (let i = 0; i < Math.max(aTokens.length, bTokens.length); i++) {
      const aToken = aTokens[i] || '';
      const bToken = bTokens[i] || '';

      // Check if tokens are numeric
      const aIsNum = /^\d+$/.test(aToken);
      const bIsNum = /^\d+$/.test(bToken);

      if (aIsNum && bIsNum) {
        // Both numeric: compare as numbers
        const diff = parseInt(aToken, 10) - parseInt(bToken, 10);
        if (diff !== 0) return diff;
      } else {
        // At least one is non-numeric: compare as strings
        const comparison = aToken.localeCompare(bToken);
        if (comparison !== 0) return comparison;
      }
    }

    return 0;
  }

  private getProfileCenter(profile: any): string {
    return (profile?.center || '').trim();
  }
}
